// ═══════════════════════════════════════════════════════════════════════════════
// Read-only history banner — ended pairing detail (§0.50)
// ═══════════════════════════════════════════════════════════════════════════════

import { USER_ROLE } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

type PreviousHelperBannerVariant = 'task' | 'receipt' | 'settlement';

type PreviousHelperBannerProps = {
  variant?: PreviousHelperBannerVariant;
};

const resolveBannerKey = (
  variant: PreviousHelperBannerVariant,
  employerUnpaired: boolean,
): string => {
  if (employerUnpaired) {
    if (variant === 'receipt') {
      return 'previousHelper.unpairedReceiptBanner';
    }
    if (variant === 'settlement') {
      return 'previousHelper.unpairedSettlementBanner';
    }
    return 'previousHelper.unpairedDetailBanner';
  }
  if (variant === 'receipt') {
    return 'previousHelper.receiptBanner';
  }
  if (variant === 'settlement') {
    return 'previousHelper.settlementBanner';
  }
  return 'previousHelper.detailBanner';
};

export const PreviousHelperBanner = ({ variant = 'task' }: PreviousHelperBannerProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const employerUnpaired =
    user?.role === USER_ROLE.EMPLOYER && !(user.pairedAt && user.pairedAt.trim() !== '');

  return (
    <div
      className="flex gap-3 rounded-xl border border-amber-100 bg-amber-50/90 p-3 dark:border-amber-900/40 dark:bg-amber-950/30"
      role="note"
      aria-live="polite"
    >
      <svg
        className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <p className="text-sm leading-relaxed text-amber-900 dark:text-amber-100/90">
        {t(resolveBannerKey(variant, employerUnpaired))}
      </p>
    </div>
  );
};
